import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { crewAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const EditCrewDialog = ({ crew, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    status: 'available',
    ratePerDay: '',
    skills: '',
    availability: '100'
  });

  useEffect(() => {
    if (crew) {
      setFormData({
        name: crew.name || '',
        role: crew.role || '',
        email: crew.email || '',
        phone: crew.phone || '',
        status: crew.status || 'available',
        ratePerDay: crew.ratePerDay?.toString() || '',
        skills: Array.isArray(crew.skills) ? crew.skills.join(', ') : '',
        availability: crew.availability?.toString() || '100'
      });
    }
  }, [crew]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await crewAPI.update(crew.id, {
        ...formData,
        ratePerDay: parseFloat(formData.ratePerDay),
        availability: parseInt(formData.availability),
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
      });
      
      toast({
        title: "Gelukt",
        description: "Personeelslid succesvol bijgewerkt"
      });
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan personeelslid niet bijwerken",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Personeelslid Bewerken</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-name">Naam *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Functie *</Label>
              <Input
                id="edit-role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="bijv. Geluidstechnicus"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-email">E-mail *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Telefoon *</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+31 6 12345678"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-ratePerDay">Dagtarief (€) *</Label>
              <Input
                id="edit-ratePerDay"
                type="number"
                step="0.01"
                value={formData.ratePerDay}
                onChange={(e) => setFormData({ ...formData, ratePerDay: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-availability">Beschikbaarheid (%) *</Label>
              <Input
                id="edit-availability"
                type="number"
                min="0"
                max="100"
                value={formData.availability}
                onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                required
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-skills">Vaardigheden (komma-gescheiden) *</Label>
              <Input
                id="edit-skills"
                value={formData.skills}
                onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                placeholder="Geluid, Licht, Rigging"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Beschikbaar</SelectItem>
                  <SelectItem value="booked">Geboekt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-yellow-600 to-red-600">
              {loading ? 'Opslaan...' : 'Wijzigingen Opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCrewDialog;
